import React, { ReactNode, useCallback, useState } from 'react';
import styled from 'styled-components';
import { DropdownActionItem, DropdownContent, DropdownList, useDropdown } from '../../../../../widgets/dropdown';

// UNA SCELTA FRA TANTE, SENZA FINESTRA (utente, 2026-10-15). «Copy to», «Replace in» e «Preview image»
// chiedono dove o da che cosa: la risposta è un elenco che si apre sotto il comando che l'ha chiesto —
// un tasto del pannello della selezione o il «…» di una riga — invece di una finestra con dei campi.
// Le tendine del Backoffice non hanno un secondo livello: quando una voce ne apre un'altra («Use
// material…»), il secondo elenco prende il posto del primo, allo stesso punto.

export type ComposerPickerEntry = {
    id: string;
    label: string;
    caption?: string;
    icon?: ReactNode;
    danger?: boolean;
    disabled?: boolean;
    onPick: () => void;
    /** Il titolo della sezione che questa voce apre: sopra le si disegna un divisore e il titolo. */
    section?: string;
    /**
     * IL COLORE DELLA SPECIE (utente, 2026-10-15): il glifo sta nel lavato del badge e ne prende l'inchiostro —
     * viola un gruppo, verde-acqua un attributo —, così il menu si legge come l'albero.
     */
    tone?: { ink: string; wash: string };
    /** Una voce che si stacca dalle altre: il nome nell'inchiostro del suo colore («Generate with AI»). */
    accent?: boolean;
    /** Una voce figlia della precedente — un'opzione sotto l'attributo —: rientrata, col filo che la lega. */
    child?: boolean;
};

const PickerTitle = styled.div`
    padding: 8px 12px 4px;
    color: var(--composer-faint, #677078);
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
`;

const SectionTitle = styled.div`
    padding: 8px 12px 2px;
    color: var(--composer-faint, #677078);
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
`;

const SectionRule = styled.hr`
    margin: 4px 8px;
    border: 0;
    border-top: 1px solid var(--composer-line, #dbe2e6);
`;

// LA VOCE FIGLIA STA SOTTO LA MADRE, RIENTRATA, col filo a gomito del prototipo: dice «dentro», non «dopo».
const ChildEntry = styled.div`
    position: relative;
    padding-left: 22px;

    &::before {
        content: '';
        position: absolute;
        top: -2px;
        left: 22px;
        width: 10px;
        height: 18px;
        border-left: 1px solid var(--composer-line, #dbe2e6);
        border-bottom: 1px solid var(--composer-line, #dbe2e6);
        border-bottom-left-radius: 6px;
    }
`;

const ToneMark = styled.span<{ $ink: string; $wash: string }>`
    display: inline-flex;
    width: 26px;
    height: 26px;
    align-items: center;
    justify-content: center;
    margin: -3px 0;
    border-radius: 6px;
    color: ${(props) => props.$ink};
    background: ${(props) => props.$wash};

    svg {
        width: 16px;
        height: 16px;
        fill: currentColor;
    }
`;

const AccentLabel = styled.span<{ $ink: string }>`
    color: ${(props) => props.$ink};
    font-weight: 600;
`;

/** Il «…» di una riga dell'albero: è lì che si apre l'elenco chiesto dal suo menu. */
export const composerRowMenuAnchor = (id: string) => {
    const row = document.querySelector<HTMLElement>(`[data-composer-row="${CSS.escape(id)}"]`);
    return row?.querySelector<HTMLElement>('[aria-label="More commands"]') ?? row;
};

export const useComposerPicker = () => {
    const [openDropdown, closeDropdown, isOpen, Dropdown] = useDropdown();
    const [state, setState] = useState<{ title?: string; entries: ComposerPickerEntry[]; fitContent?: boolean }>({ entries: [] });

    const openPicker = useCallback(
        (anchor: HTMLElement | null | undefined, entries: ComposerPickerEntry[], title?: string, fitContent?: boolean) => {
            if (!anchor) return;
            setState({ title, entries, fitContent });
            openDropdown(anchor, 'bottom-start');
        },
        [openDropdown]
    );

    const picker = isOpen ? (
        // UN MENU DI COMANDI SI VEDE TUTTO (utente, 2026-10-15): solo un elenco di cose — gli attributi di «Copy to» —
        // scorre oltre una misura; il menu di «Add» resta intero, fino all'altezza della finestra.
        <Dropdown minWidth='240px' maxHeight={state.fitContent ? undefined : '340px'}>
            <DropdownContent onMouseDown={(event) => event.stopPropagation()} onClick={(event) => event.stopPropagation()}>
                {state.title && <PickerTitle>{state.title}</PickerTitle>}
                <DropdownList>
                    {state.entries.map((entry, index) => {
                        const item = (
                            <DropdownActionItem
                                key={entry.id}
                                icon={
                                    entry.tone && entry.icon ? (
                                        <ToneMark $ink={entry.tone.ink} $wash={entry.tone.wash}>
                                            {entry.icon}
                                        </ToneMark>
                                    ) : (
                                        entry.icon
                                    )
                                }
                                caption={entry.caption}
                                danger={entry.danger}
                                disabled={entry.disabled}
                                onClick={() => {
                                    // Prima si chiude, poi si sceglie: una scelta che apre un secondo elenco lo apre qui.
                                    closeDropdown();
                                    entry.onPick();
                                }}
                            >
                                {entry.accent && entry.tone ? <AccentLabel $ink={entry.tone.ink}>{entry.label}</AccentLabel> : entry.label}
                            </DropdownActionItem>
                        );
                        return (
                            <React.Fragment key={entry.id}>
                                {entry.section && index > 0 && <SectionRule />}
                                {entry.section && <SectionTitle>{entry.section}</SectionTitle>}
                                {entry.child ? <ChildEntry>{item}</ChildEntry> : item}
                            </React.Fragment>
                        );
                    })}
                </DropdownList>
            </DropdownContent>
        </Dropdown>
    ) : null;

    return { openPicker, picker };
};
